import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { inhaltsBox, type SeitenAnsichtLike, type ViewerLike } from "./EntfernenEbene";
import type { BearbeitungAktion, ErsetzenEintrag, PdfTextErsetzen } from "./pdfBearbeitung";
import { pdfZuCssRect, type ViewportLike } from "./pdfKoordinaten";
import { nichtDarstellbare } from "./winAnsi";
import {
    bildeZeilen,
    RICHTUNG,
    zeileAnPunkt,
    zeilenId,
    zeilenMasse,
    type FontStyleLike,
    type TextItemLike,
    type Zeile,
} from "./zeilen";
import { aktivesDokument } from "../../windowManager/browserFenster/fensterRegister";

/**
 * Text-Ebene für „Text bearbeiten“ (PO 28.09.2026): Man klickt auf eine
 * Textzeile im PDF, und GENAU DORT wird sie zu einem Kasten, in dem man tippt —
 * gleiche Stelle, gleiche Größe, ähnliche Schrift. Kein Popup, kein Dialog,
 * kein `<input>`: der Kasten ist ein `contentEditable` direkt auf der Seite
 * (wie das Umbenennen in `DateinameKasten` und das Textwerkzeug in
 * `tickets/screenshot/MarkierEditor.tsx`).
 *
 * - Enter oder Klick daneben übernimmt, Escape verwirft.
 * - Übernommene Änderungen bleiben als weißer Kasten mit dem neuen Text
 *   sichtbar; Klick öffnet ihn wieder, × nimmt die Änderung zurück.
 * - Geschrieben wird erst beim Speichern auf dem Server: MuPDF entfernt die
 *   alte Zeile (nur den Text), pdf-lib schreibt den neuen.
 *
 * Wie die Entfernen-Ebene hängt alles per Portal in den `.page`-Divs von PDF.js
 * und wird nach Zoom/Drehung neu berechnet.
 */

/** Der Teil einer PDF.js-Seite (`PDFPageProxy`), den die Ebene braucht. */
export interface PdfSeiteLike {
    getTextContent(): Promise<{ items: unknown[]; styles: Record<string, FontStyleLike> }>;
    commonObjs?: { has(id: string): boolean; get(id: string): unknown };
}
/** Der Teil des `PDFDocumentProxy`, den die Ebene braucht (Seiten 1-basiert). */
export interface PdfLike {
    getPage(nummer: number): Promise<PdfSeiteLike>;
}

// T-216: Kopieren geht trotzdem — über den erkannten Text im Seitenfeld.
export const SCAN_HINWEIS =
    "Diese Seite ist ein Scan — hier gibt es keinen Text zum Bearbeiten. Den erkannten Text finden Sie unter „Text kopieren“.";
const LESEFEHLER_HINWEIS = "Der Text dieser Seite konnte nicht gelesen werden.";
/** Eingaben, die aus einer Zeile mehr machen würden als Text. */
const GESPERRTE_EINGABEN = new Set(["formatBold", "formatItalic", "formatUnderline", "insertFromDrop"]);

/**
 * `plaintext-only` hält Formatierung (Strg+B, eingefügtes HTML) schon im
 * Browser fern; wer es nicht kennt (ältere Browser, jsdom), bekommt `true` —
 * dann sperren `beforeinput`/`paste`/`drop` unten dasselbe.
 */
let editierModus: "plaintext-only" | "true" | undefined;
const bearbeitbar = (): "plaintext-only" | "true" => {
    if (!editierModus) {
        try {
            const probe = document.createElement("span");
            probe.contentEditable = "plaintext-only";
            editierModus = probe.contentEditable === "plaintext-only" ? "plaintext-only" : "true";
        } catch {
            editierModus = "true";
        }
    }
    return editierModus;
};
const ZEICHEN_HINWEIS = "Dieses Zeichen kann die PDF-Standardschrift nicht darstellen";
const RENDER_EREIGNISSE = ["pagerendered", "textlayerrendered", "scalechanging", "rotationchanging", "pagesinit"];
/** Solange das Werkzeug aktiv ist: Textauswahl und Links von PDF.js aus, Klicks gehen an die Seite. */
const AKTIV_KLASSE = "kmvs-text-bearbeiten-aktiv";
const AKTIV_CSS = `.${AKTIV_KLASSE} .textLayer, .${AKTIV_KLASSE} .textLayer *, .${AKTIV_KLASSE} .annotationLayer, .${AKTIV_KLASSE} .annotationLayer * { pointer-events: none !important; }
.${AKTIV_KLASSE} .page { cursor: text; }`;

const SCHRIFT_CSS: Record<PdfTextErsetzen["schrift"], string> = {
    sans: "Helvetica, Arial, sans-serif",
    serif: '"Times New Roman", Times, serif',
    mono: '"Courier New", Courier, monospace',
};

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Was an einer Zeile zum Zeichnen nötig ist — `Zeile` und `ErsetzenEintrag` haben beides. */
type ZeilenLage = Pick<PdfTextErsetzen, "rect" | "x" | "y" | "groesse" | "drehung" | "schrift" | "fett">;

/**
 * Grundlinien-Start auf dem Bildschirm (CSS-px relativ zur Seite), Winkel der
 * Schreibrichtung dort und px je pt — berücksichtigt Zoom und Ansicht-Drehung.
 */
function bildschirmLage(v: ViewportLike, z: ZeilenLage) {
    const punkt = (x: number, y: number) => {
        const r = v.convertToViewportRectangle([x, y, x, y]);
        return [r[0], r[1]];
    };
    const [dx, dy] = RICHTUNG[z.drehung];
    const [px, py] = punkt(z.x, z.y);
    const [qx, qy] = punkt(z.x + dx, z.y + dy);
    const winkel = Math.round((Math.atan2(qy - py, qx - px) * 180) / Math.PI);
    return { left: r2(px), top: r2(py), winkel: ((winkel % 360) + 360) % 360, skala: Math.hypot(qx - px, qy - py) };
}

/** Kasten-Stil über der alten Zeile: Oberkante = Oberlänge, gleiche Schrift und Größe (× Zoom). */
function kastenStil(z: ZeilenLage, skala: number): React.CSSProperties {
    const m = zeilenMasse(z);
    const hoehe = r2((m.oben + m.unten) * skala);
    return {
        position: "absolute",
        left: 0,
        top: r2(-m.oben * skala),
        height: hoehe,
        lineHeight: `${hoehe}px`,
        minWidth: r2(m.breite * skala),
        fontSize: `${r2(z.groesse * skala)}px`,
        fontFamily: SCHRIFT_CSS[z.schrift],
        fontWeight: z.fett ? 700 : 400,
        whiteSpace: "pre",
        color: "#000",
        // Weiß verdeckt die alte Zeile — man sieht nur, was man tippt.
        background: "#fff",
        padding: 0,
        margin: 0,
        boxSizing: "content-box",
        pointerEvents: "auto",
    };
}

const stil = {
    lage: (l: ReturnType<typeof bildschirmLage>): React.CSSProperties => ({
        position: "absolute",
        left: l.left,
        top: l.top,
        width: 0,
        height: 0,
        transformOrigin: "0 0",
        ...(l.winkel ? { transform: `rotate(${l.winkel}deg)` } : null),
    }),
    hover: {
        position: "absolute",
        boxSizing: "border-box",
        border: "1px solid rgba(0, 95, 184, 0.9)",
        background: "rgba(0, 95, 184, 0.06)",
        pointerEvents: "auto",
        cursor: "text",
    } as React.CSSProperties,
    kreuz: {
        position: "absolute",
        top: -9,
        right: -9,
        width: 18,
        height: 18,
        borderRadius: 9,
        border: "1px solid rgba(0, 95, 184, 0.9)",
        background: "#fff",
        color: "rgb(0, 95, 184)",
        fontSize: 12,
        lineHeight: "16px",
        padding: 0,
        cursor: "pointer",
        pointerEvents: "auto",
        fontFamily: "inherit",
        fontWeight: 400,
    } as React.CSSProperties,
    zeichenHinweis: {
        position: "absolute",
        left: 0,
        whiteSpace: "nowrap",
        fontSize: 11,
        fontFamily: "Segoe UI, sans-serif",
        color: "rgb(180, 20, 20)",
        background: "#fff",
        border: "1px solid rgba(180, 20, 20, 0.6)",
        borderRadius: 3,
        padding: "1px 4px",
        pointerEvents: "none",
    } as React.CSSProperties,
};

/** Ergebnis des Textlesens einer Seite. */
interface SeitenText {
    zeilen: Zeile[];
    /** Lesen gelang, aber die Seite hat keinen Text (Scan). */
    textlos: boolean;
    /** `getTextContent()` ist gescheitert. */
    fehler: boolean;
}

/** Kasten, der gerade offen ist. */
interface Offen {
    id: string;
    lage: ZeilenLage & { seite: number };
    /** Text beim Öffnen — unverändert übernehmen tut nichts. */
    start: string;
    /** Text der Zeile im PDF (falls bekannt) — wer ihn zurücktippt, nimmt die Änderung zurück. */
    original?: string;
    /** Klickstelle (Fenster-Koordinaten) für den Cursor. */
    klick?: { x: number; y: number };
    /** Zählt jedes Öffnen — auch dieselbe Zeile erneut setzt Inhalt und Fokus neu. */
    nr: number;
}

/** Inhalt des Kastens → Text: Umbrüche/Tabs/geschützte Leerzeichen → Leerzeichen, rechts gekürzt. */
const kastenText = (el: HTMLElement | null) =>
    (el?.textContent ?? "").replace(/\u00a0|\t/g, " ").replace(/[\r\n]+/g, " ").replace(/\s+$/, "");

/** Cursor an die Klickstelle, sonst ans Ende. */
function setzeCursor(el: HTMLElement, klick?: { x: number; y: number }) {
    const sel = window.getSelection?.();
    if (!sel) return;
    const range = aktivesDokument().createRange();
    let gesetzt = false;
    if (klick) {
        const doc = document as Document & {
            caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
            caretRangeFromPoint?: (x: number, y: number) => Range | null;
        };
        const pos = doc.caretPositionFromPoint?.(klick.x, klick.y);
        if (pos && el.contains(pos.offsetNode)) {
            range.setStart(pos.offsetNode, pos.offset);
            gesetzt = true;
        } else {
            const r = doc.caretRangeFromPoint?.(klick.x, klick.y);
            if (r && el.contains(r.startContainer)) {
                range.setStart(r.startContainer, r.startOffset);
                gesetzt = true;
            }
        }
    }
    if (!gesetzt) {
        range.selectNodeContents(el);
        range.collapse(false);
    } else {
        range.collapse(true);
    }
    sel.removeAllRanges();
    sel.addRange(range);
}

export const TextEbene: React.FC<{
    viewer: ViewerLike | null;
    /** Scrollender Container des Viewers — hier kommen Klicks und Mausbewegungen an. */
    container: HTMLElement | null;
    pdf: PdfLike | null;
    /** Werkzeug „Text bearbeiten“ gewählt? Übernommene Änderungen sind immer sichtbar. */
    aktiv: boolean;
    ersetzen: readonly ErsetzenEintrag[];
    dispatch: React.Dispatch<BearbeitungAktion>;
    /** Hinweis für die Leiste (z. B. Scan-Seite); `null` löscht ihn. */
    onHinweis?: (text: string | null) => void;
}> = ({ viewer, container, pdf, aktiv, ersetzen, dispatch, onHinweis }) => {
    const [, neuZeichnen] = React.useReducer((n: number) => n + 1, 0);
    const [hover, setHover] = React.useState<Zeile | null>(null);
    const [offen, setOffenState] = React.useState<Offen | null>(null);
    const [fremde, setFremde] = React.useState<string[]>([]);
    const offenRef = React.useRef<Offen | null>(null);
    const setOffen = (o: Offen | null) => {
        offenRef.current = o;
        setOffenState(o);
    };
    const kastenRef = React.useRef<HTMLSpanElement | null>(null);
    // Escape schließt den Kasten; ein dabei nachgereichtes Blur darf den
    // getippten Text dann nicht doch noch übernehmen (wie `DateinameKasten`).
    const verworfen = React.useRef(false);
    const oeffnungen = React.useRef(0);
    const hosts = React.useRef(new Map<number, HTMLDivElement>());
    const benutzteSeiten = React.useRef(new Set<number>());

    // ─── Zeilen je Seite (lazy, einmal je Dokument) ────────────────────────
    const zeilenCache = React.useRef(new Map<number, Promise<SeitenText>>());
    const geladen = React.useRef(new Map<number, Zeile[]>());
    React.useEffect(() => {
        zeilenCache.current = new Map();
        geladen.current = new Map();
    }, [pdf]);

    const zeilenVon = React.useCallback(
        (seite: number): Promise<SeitenText> => {
            if (!pdf) return Promise.resolve({ zeilen: [], textlos: false, fehler: true });
            let p = zeilenCache.current.get(seite);
            if (!p) {
                const cache = geladen.current;
                p = pdf
                    .getPage(seite + 1)
                    .then(async (page) => {
                        const inhalt = await page.getTextContent();
                        // Echter Schriftname (z. B. „ABCDEF+Arial-BoldMT“) — nur daran erkennt man Fettschrift.
                        const namen = (fontName: string) => {
                            try {
                                if (!page.commonObjs?.has(fontName)) return undefined;
                                const f = page.commonObjs.get(fontName) as { name?: string; bold?: boolean; black?: boolean } | undefined;
                                return [f?.name, f?.bold ? "Bold" : "", f?.black ? "Black" : ""].filter(Boolean).join(" ") || undefined;
                            } catch {
                                return undefined;
                            }
                        };
                        const items = inhalt.items as TextItemLike[];
                        return {
                            zeilen: bildeZeilen(seite, items, inhalt.styles, namen),
                            // „Scan“ nur, wenn die Seite wirklich keinen Text hat.
                            textlos: !items.some((it) => typeof it?.str === "string" && it.str.trim() !== ""),
                            fehler: false,
                        };
                    })
                    .catch((): SeitenText => {
                        // Nicht merken — beim nächsten Klick neu versuchen.
                        zeilenCache.current.delete(seite);
                        return { zeilen: [], textlos: false, fehler: true };
                    })
                    .then((ergebnis) => {
                        if (!ergebnis.fehler) cache.set(seite, ergebnis.zeilen);
                        return ergebnis;
                    });
                zeilenCache.current.set(seite, p);
            }
            return p;
        },
        [pdf],
    );

    const seitenAnsicht = React.useCallback(
        (i: number): SeitenAnsichtLike | null => (viewer ? viewer.getPageView(i) ?? null : null),
        [viewer],
    );

    // Nach Zoom/Drehung/Neuzeichnen die Kästen neu berechnen.
    React.useEffect(() => {
        if (!viewer) return;
        const bus = viewer.eventBus;
        const fn = () => neuZeichnen();
        for (const e of RENDER_EREIGNISSE) bus.on(e, fn);
        return () => {
            for (const e of RENDER_EREIGNISSE) bus.off(e, fn);
        };
    }, [viewer]);

    // Aufräumen beim Verlassen des Editors.
    React.useEffect(() => {
        const map = hosts.current;
        return () => {
            for (const h of map.values()) h.remove();
            map.clear();
        };
    }, []);

    const hostFuer = (i: number): HTMLDivElement => {
        let host = hosts.current.get(i);
        if (!host) {
            host = document.createElement("div");
            host.className = "kmvs-text-ebene";
            Object.assign(host.style, { position: "absolute", inset: "0", pointerEvents: "none", zIndex: "21" });
            hosts.current.set(i, host);
        }
        return host;
    };

    // Nach jedem Render: Hosts benutzter Seiten (wieder) einhängen — PDF.js
    // leert die Seiten-Divs beim Zoomen —, die übrigen aushängen.
    React.useLayoutEffect(() => {
        for (const [i, host] of hosts.current) {
            const div = benutzteSeiten.current.has(i) ? seitenAnsicht(i)?.div : null;
            if (div && host.parentNode !== div) div.appendChild(host);
            else if (!div && host.parentNode) host.remove();
        }
    });

    // Werkzeug aktiv: Textauswahl/Links von PDF.js aus, Cursor „Text“.
    React.useEffect(() => {
        if (!container || !aktiv) return;
        container.classList.add(AKTIV_KLASSE);
        return () => container.classList.remove(AKTIV_KLASSE);
    }, [aktiv, container]);

    // ─── Öffnen / Übernehmen / Verwerfen ───────────────────────────────────

    const oeffne = React.useCallback(
        (lage: ZeilenLage & { seite: number }, id: string, start: string, original?: string, klick?: { x: number; y: number }) => {
            verworfen.current = false;
            setFremde(nichtDarstellbare(start));
            setHover(null);
            onHinweis?.(null);
            setOffen({ id, lage, start, original, klick, nr: ++oeffnungen.current });
        },
        [onHinweis],
    );

    const uebernehmen = React.useCallback(() => {
        const o = offenRef.current;
        if (!o || verworfen.current) return;
        const text = kastenText(kastenRef.current);
        setOffen(null);
        setFremde([]);
        if (text === o.start) return;
        const bestehend = ersetzen.some((e) => e.id === o.id);
        if (o.original !== undefined && text === o.original) {
            // Originaltext zurückgetippt → keine Änderung mehr.
            if (bestehend) dispatch({ typ: "ersetzenWeg", id: o.id });
            return;
        }
        const { rect, x, y, groesse, schrift, fett, drehung } = o.lage;
        dispatch({
            typ: "ersetzenSetzen",
            eintrag: { id: o.id, seite: o.lage.seite, rect: [...rect], text, x, y, groesse, schrift, fett, drehung },
        });
    }, [dispatch, ersetzen]);

    const verwerfen = () => {
        verworfen.current = true;
        setOffen(null);
        setFremde([]);
    };

    // Werkzeug abgewählt, während ein Kasten offen ist → übernehmen.
    React.useEffect(() => {
        if (!aktiv) {
            if (offenRef.current) uebernehmen();
            setHover(null);
        }
    }, [aktiv, uebernehmen]);

    // Beim Öffnen: Inhalt setzen, Fokus, Cursor an die Klickstelle. Danach
    // gehört der Inhalt dem Kasten — React schreibt nicht hinein.
    React.useLayoutEffect(() => {
        const el = kastenRef.current;
        if (!offen || !el) return;
        el.textContent = offen.start;
        el.focus();
        setzeCursor(el, offen.klick);
        // Formatierung und Ablegen per Maus sperren (React kennt `inputType` nicht).
        const sperre = (ev: Event) => {
            if (GESPERRTE_EINGABEN.has((ev as InputEvent).inputType)) ev.preventDefault();
        };
        el.addEventListener("beforeinput", sperre);
        return () => el.removeEventListener("beforeinput", sperre);
        // Nur beim Öffnen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offen?.nr]);

    // ─── Klick und Hover auf der Seite ─────────────────────────────────────

    /** Seite und Userspace-Punkt unter dem Mauszeiger. */
    const punktUnter = React.useCallback(
        (e: MouseEvent): { seite: number; x: number; y: number } | null => {
            const ziel = e.target as HTMLElement | null;
            if (!ziel || typeof ziel.closest !== "function") return null;
            if (ziel.closest("[data-text-kasten]")) return null; // eigener Kasten/Anzeige
            const seitenDiv = ziel.closest<HTMLElement>(".page[data-page-number]");
            if (!seitenDiv) return null;
            const seite = Number(seitenDiv.dataset.pageNumber) - 1;
            const ansicht = seitenAnsicht(seite);
            if (!ansicht) return null;
            const box = inhaltsBox(seitenDiv);
            const [x, y] = ansicht.viewport.convertToPdfPoint(e.clientX - box.left, e.clientY - box.top);
            return { seite, x, y };
        },
        [seitenAnsicht],
    );

    React.useEffect(() => {
        if (!container || !aktiv || !viewer) return;
        const onClick = (e: MouseEvent) => {
            if (e.button !== 0) return;
            const p = punktUnter(e);
            if (!p) return;
            const klick = { x: e.clientX, y: e.clientY };
            void zeilenVon(p.seite).then(({ zeilen, textlos, fehler }) => {
                if (fehler) {
                    onHinweis?.(LESEFEHLER_HINWEIS);
                    return;
                }
                if (textlos) {
                    onHinweis?.(SCAN_HINWEIS);
                    return;
                }
                // Seite mit Text: ein alter Hinweis (Scan, Lesefehler) hat sich erledigt
                // und soll den Änderungs-Zähler nicht länger verdecken.
                onHinweis?.(null);
                const z = zeileAnPunkt(zeilen, p.x, p.y);
                if (!z) return;
                const id = zeilenId(z);
                const eintrag = ersetzen.find((x) => x.id === id);
                oeffne(z, id, eintrag ? eintrag.text : z.text, z.text, klick);
            });
        };
        const onMove = (e: MouseEvent) => {
            if (offenRef.current) return;
            const p = punktUnter(e);
            if (!p) {
                // Über dem eigenen Kasten/Rahmen den Rahmen stehen lassen.
                const ziel = e.target as HTMLElement | null;
                if (!ziel?.closest?.(".kmvs-text-ebene")) setHover(null);
                return;
            }
            const zeilen = geladen.current.get(p.seite);
            if (!zeilen) {
                void zeilenVon(p.seite);
                return;
            }
            const z = zeileAnPunkt(zeilen, p.x, p.y);
            setHover((alt) => (alt && z && zeilenId(alt) === zeilenId(z) ? alt : z));
        };
        const onLeave = () => setHover(null);
        container.addEventListener("click", onClick);
        container.addEventListener("mousemove", onMove);
        container.addEventListener("mouseleave", onLeave);
        return () => {
            container.removeEventListener("click", onClick);
            container.removeEventListener("mousemove", onMove);
            container.removeEventListener("mouseleave", onLeave);
        };
    }, [aktiv, container, ersetzen, oeffne, onHinweis, punktUnter, viewer, zeilenVon]);

    /** Übernommene Änderung wieder öffnen — Originaltext der Zeile nachschlagen. */
    const eintragOeffnen = (e: ErsetzenEintrag, klick: { x: number; y: number }) => {
        void zeilenVon(e.seite).then(({ zeilen }) => {
            const original = zeilen.find((z) => zeilenId(z) === e.id)?.text;
            oeffne(e, e.id, e.text, original, klick);
        });
    };

    // ─── Zeichnen ──────────────────────────────────────────────────────────

    if (!viewer) return null;

    const jeSeite = new Map<number, ErsetzenEintrag[]>();
    for (const e of ersetzen) jeSeite.set(e.seite, [...(jeSeite.get(e.seite) ?? []), e]);
    if (offen && !jeSeite.has(offen.lage.seite)) jeSeite.set(offen.lage.seite, []);
    if (aktiv && hover && !jeSeite.has(hover.seite)) jeSeite.set(hover.seite, []);
    benutzteSeiten.current = new Set(jeSeite.keys());

    return (
        <>
            {aktiv && <style>{AKTIV_CSS}</style>}
            {[...jeSeite].map(([seite, liste]) => {
                const ansicht = seitenAnsicht(seite);
                if (!ansicht?.div) return null;
                const v = ansicht.viewport;
                return createPortal(
                    <>
                        {aktiv && hover && hover.seite === seite && !offen && (() => {
                            const box = pdfZuCssRect(v, hover.rect);
                            return (
                                <div
                                    title="Klicken zum Bearbeiten"
                                    style={{ ...stil.hover, left: box.left, top: box.top, width: box.width, height: box.height }}
                                />
                            );
                        })()}
                        {liste
                            .filter((e) => e.id !== offen?.id)
                            .map((e) => {
                                const l = bildschirmLage(v, e);
                                return (
                                    <div key={e.id} data-text-kasten="" style={stil.lage(l)}>
                                        <span
                                            title={aktiv ? "Geänderte Zeile — klicken, um weiterzuschreiben" : "Wird beim Speichern so ins PDF geschrieben"}
                                            data-ersetzt={e.id}
                                            onClick={(ev) => {
                                                if (!aktiv) return;
                                                ev.stopPropagation();
                                                eintragOeffnen(e, { x: ev.clientX, y: ev.clientY });
                                            }}
                                            style={{
                                                ...kastenStil(e, l.skala),
                                                outline: "1px dashed rgba(0, 95, 184, 0.9)",
                                                cursor: "text",
                                                // Nur im Werkzeug „Text bearbeiten“ klickbar — sonst gehören
                                                // Klicks den anderen Werkzeugen (Markieren, Entfernen …).
                                                pointerEvents: aktiv ? "auto" : "none",
                                            }}
                                        >
                                            {e.text}
                                            {aktiv && (
                                            <button
                                                type="button"
                                                aria-label="Änderung zurücknehmen"
                                                title="Änderung zurücknehmen — die alte Zeile bleibt"
                                                style={stil.kreuz}
                                                onPointerDown={(ev) => ev.stopPropagation()}
                                                onClick={(ev) => {
                                                    ev.stopPropagation();
                                                    dispatch({ typ: "ersetzenWeg", id: e.id });
                                                }}
                                            >
                                                ×
                                            </button>
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                        {offen && offen.lage.seite === seite && (() => {
                            const l = bildschirmLage(v, offen.lage);
                            const m = zeilenMasse(offen.lage);
                            return (
                                <div key={`offen-${offen.nr}`} data-text-kasten="" style={stil.lage(l)}>
                                    <span
                                        ref={kastenRef}
                                        contentEditable={bearbeitbar() as unknown as boolean}
                                        suppressContentEditableWarning
                                        spellCheck={false}
                                        aria-label="Zeile bearbeiten"
                                        title="Enter oder Klick daneben übernimmt, Escape verwirft"
                                        style={{
                                            ...kastenStil(offen.lage, l.skala),
                                            outline: fremde.length
                                                ? "2px solid rgb(180, 20, 20)"
                                                : "2px solid rgba(0, 95, 184, 0.9)",
                                            cursor: "text",
                                        }}
                                        onBlur={() => uebernehmen()}
                                        onInput={(ev) => setFremde(nichtDarstellbare(kastenText(ev.currentTarget)))}
                                        onPaste={(ev) => {
                                            // Nur Text, eine Zeile — eingefügtes HTML oder Umbrüche haben hier nichts verloren.
                                            ev.preventDefault();
                                            const text = ev.clipboardData.getData("text/plain").replace(/\s*[\r\n]+\s*/g, " ").replace(/\t/g, " ");
                                            aktivesDokument().execCommand("insertText", false, text);
                                        }}
                                        onDrop={(ev) => ev.preventDefault()}
                                        onKeyDown={(ev) => {
                                            // Tasten im Kasten bleiben im Kasten (kein Entf-Markieren, keine Viewer-Kürzel).
                                            ev.stopPropagation();
                                            // Während einer IME-Eingabe (Tastatur-Assistent, asiatische
                                            // Eingabe) gehören Enter/Escape der IME, nicht dem Kasten.
                                            if (ev.nativeEvent.isComposing || ev.keyCode === 229) return;
                                            if (ev.key === "Tab") {
                                                // Kein Fokuswechsel — ein Tab ist in einer PDF-Zeile ein Leerzeichen.
                                                ev.preventDefault();
                                                document.execCommand?.("insertText", false, " ");
                                                return;
                                            }
                                            if (ev.key === "Enter") {
                                                ev.preventDefault();
                                                uebernehmen();
                                            } else if (ev.key === "Escape") {
                                                ev.preventDefault();
                                                verwerfen();
                                            }
                                        }}
                                    />
                                    {fremde.length > 0 && (
                                        <div style={{ ...stil.zeichenHinweis, top: r2(m.unten * l.skala + 3) }}>
                                            {ZEICHEN_HINWEIS}: {fremde.join(" ")} — wird als „?“ gespeichert
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </>,
                    hostFuer(seite),
                    `text-seite-${seite}`,
                );
            })}
        </>
    );
};
